/** @jsxImportSource @emotion/react */
import { EuiFlexGroup, EuiFlexItem } from "@inscopix/ideas-eui";
import { useDataTableContext } from "../../store/DataTableProvider";
import { QuickActionInsertRow } from "./QuickActionInsertRow";
import { QuickActionGoToTaskInfo } from "./QuickActionGoToTaskInfo";
import { QuickActionGoToResults } from "./QuickActionGoToResults";
import { QuickActionJumpToColumn } from "./QuickActionJumpToColumn";
import { QuickActionRunSelectedRows } from "./QuickActionRunRow";
import { QuickActionDeleteSelectedRows } from "./QuickActionDeleteRow";
import { css } from "@emotion/react";
import { QuickActionAddColumn } from "./QuickActionAddColumns";
import { QuickActionInsertColumnsFromTable } from "./QuickActionInsertColumnsFromTable";
import { QuickActionInsertNewAnalysisTable } from "./QuickActionInsertNewAnalysis";
import { QuickActionToggleNotes } from "./QuickActionToggleNotes";

const styles = {
  buttonGroup: css`
    margin-right: 2px;
    padding-right: 2px;
    display: inline-block;
    border-right: 1px solid #eee;
  `,
};

const ButtonGroup = ({
  style,
  children,
}: {
  style?: React.CSSProperties;
  children: React.ReactNode;
}) => (
  <div css={styles.buttonGroup} style={style}>
    {children}
  </div>
);

export interface DataTableQuickActionBarProps {
  hasNotes?: boolean;
}

/**
 * Component that renders groups of buttons that manipulate a data table through
 * single-click actions
 */
export const DataTableQuickActionBar = ({
  hasNotes = true,
}: DataTableQuickActionBarProps) => {
  const selectedTable = useDataTableContext((s) => {
    return s.tables.find(({ id }) => id === s.selectedTableId);
  });

  return (
    <EuiFlexGroup responsive={false} alignItems="center">
      <EuiFlexItem>
        <div
          // don't wrap quick action buttons
          css={css`
            white-space: nowrap;
          `}
        >
          <ButtonGroup
            style={
              // match group width to display over ID column
              selectedTable?.kind === "data"
                ? {
                    width: "74px",
                  }
                : undefined
            }
          >
            <QuickActionInsertRow />
            <QuickActionDeleteSelectedRows />
          </ButtonGroup>
          <ButtonGroup>
            {selectedTable?.kind === "analysis" && (
              <QuickActionRunSelectedRows />
            )}
          </ButtonGroup>
          <ButtonGroup>
            {selectedTable?.kind === "data" && <QuickActionAddColumn />}
            {selectedTable?.kind === "data" && (
              <QuickActionInsertColumnsFromTable />
            )}
            {selectedTable?.kind === "data" && (
              <QuickActionInsertNewAnalysisTable />
            )}
          </ButtonGroup>
        </div>
      </EuiFlexItem>

      <EuiFlexItem grow={false}>
        <EuiFlexGroup responsive={false}>
          <EuiFlexItem grow={false}>
            <QuickActionJumpToColumn />
          </EuiFlexItem>
        </EuiFlexGroup>
      </EuiFlexItem>

      <EuiFlexItem>
        <EuiFlexGroup
          responsive={false}
          gutterSize="s"
          justifyContent="flexEnd"
        >
          {selectedTable?.kind === "analysis" && (
            <EuiFlexItem grow={false}>
              <QuickActionGoToResults />
            </EuiFlexItem>
          )}

          {selectedTable?.kind === "analysis" && (
            <EuiFlexItem grow={false}>
              <QuickActionGoToTaskInfo />
            </EuiFlexItem>
          )}

          {hasNotes && (
            <EuiFlexItem grow={false}>
              <QuickActionToggleNotes />
            </EuiFlexItem>
          )}
        </EuiFlexGroup>
      </EuiFlexItem>
    </EuiFlexGroup>
  );
};
